import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import AdPreviewModal from "./AdPreviewModal";
import {
  Plus,
  ExternalLink,
  Pencil,
  Trash2,
  ChevronLeft,
  Image,
  Loader2,
  Layers,
  Eye,
  Check,
  Search,
} from "lucide-react";

const CAMPAIGN_TYPES = [
  { value: "facebook", label: "Facebook / Instagram Ads", color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  { value: "tiktok", label: "TikTok Ads", color: "bg-pink-500/20 text-pink-300 border-pink-500/30" },
  { value: "mobile_conquesting", label: "Mobile Conquesting", color: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30" },
  { value: "social_mirror", label: "Social Mirror Ads", color: "bg-teal-500/20 text-teal-300 border-teal-500/30" },
  { value: "ctv", label: "Connected TV", color: "bg-purple-500/20 text-purple-300 border-purple-500/30" },
  { value: "general", label: "General / Other", color: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
];

function getCampaignBadge(type) {
  return CAMPAIGN_TYPES.find((c) => c.value === type) || CAMPAIGN_TYPES[4];
}

export default function AdCreative({ clients, API }) {
  const [selectedClient, setSelectedClient] = useState(null);
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [previewCreative, setPreviewCreative] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ label: "", url: "", campaign_type: "general", ad_name: "", notes: "" });
  const [adNames, setAdNames] = useState([]);
  // Index of currently-imported ad names per campaign type, so we can flag
  // creative links whose ad_name no longer exists in the imported data
  // (usually because data was re-imported with different ad-name formatting).
  const [importedAdNamesByType, setImportedAdNamesByType] = useState({});

  const fetchCreatives = useCallback(async (clientId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ad-creatives?client_id=${clientId}`);
      setCreatives(res.data);
    } catch (e) {
      console.error("Error fetching ad creatives:", e);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    if (selectedClient) {
      fetchCreatives(selectedClient.id);
    }
  }, [selectedClient, fetchCreatives]);

  // Index every ad name currently in the imported data, per platform, so the
  // gallery can flag orphaned creative links (ad_name points to a name that
  // no longer exists because data was re-imported with different formatting).
  useEffect(() => {
    if (!selectedClient) return;
    const types = ["facebook", "mobile_conquesting", "social_mirror", "ctv", "tiktok"];
    const load = async () => {
      const result = {};
      await Promise.all(types.map(async (t) => {
        try {
          const res = await axios.get(`${API}/ad-names?client_id=${selectedClient.id}&campaign_type=${t}`);
          result[t] = new Set((Array.isArray(res.data) ? res.data : []).map((n) => n.trim().toLowerCase()));
        } catch {
          result[t] = new Set();
        }
      }));
      setImportedAdNamesByType(result);
    };
    load();
  }, [selectedClient, creatives, API]);

  // Fetch available ad names (with impressions) when campaign type changes
  useEffect(() => {
    if (!selectedClient || !form.campaign_type || form.campaign_type === "general") {
      setAdNames([]);
      return;
    }
    const fetchAdNames = async () => {
      try {
        const res = await axios.get(`${API}/ad-names?client_id=${selectedClient.id}&campaign_type=${form.campaign_type}&with_stats=true`);
        setAdNames(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        setAdNames([]);
      }
    };
    fetchAdNames();
  }, [selectedClient, form.campaign_type, API]);

  const handleSave = async () => {
    if (!form.label.trim() || !form.url.trim()) {
      toast.error("Label and URL are required");
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/ad-creatives/${editingId}`, form);
        toast.success("Updated");
      } else {
        await axios.post(`${API}/ad-creatives`, {
          client_id: selectedClient.id,
          ...form,
        });
        toast.success("Ad creative link added");
      }
      setForm({ label: "", url: "", campaign_type: "general", ad_name: "", notes: "" });
      setShowAdd(false);
      setEditingId(null);
      fetchCreatives(selectedClient.id);
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/ad-creatives/${id}`);
      toast.success("Removed");
      fetchCreatives(selectedClient.id);
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({ label: c.label, url: c.url, campaign_type: c.campaign_type || "general", ad_name: c.ad_name || "", notes: c.notes || "" });
    setShowAdd(true);
  };

  const cancelForm = () => {
    setShowAdd(false);
    setEditingId(null);
    setForm({ label: "", url: "", campaign_type: "general", ad_name: "", notes: "" });
  };

  // Group creatives by campaign type
  const grouped = CAMPAIGN_TYPES.reduce((acc, type) => {
    const items = creatives.filter((c) => (c.campaign_type || "general") === type.value);
    if (items.length > 0) acc.push({ ...type, items });
    return acc;
  }, []);

  // Client selection screen
  if (!selectedClient) {
    return (
      <div data-testid="ad-creative-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">Ad Creative</h1>
          <p className="text-slate-500 mt-1">View and manage your Adpiler ad previews by client</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((c) => (
            <motion.button
              key={c.id}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedClient(c)}
              className="bg-white rounded-xl p-5 border border-slate-200 text-left hover:border-violet-300 hover:shadow-lg hover:shadow-violet-500/5 transition-all group"
              data-testid={`ad-creative-client-${c.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                  <Image className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate group-hover:text-violet-700 transition-colors">
                    {c.name}
                  </p>
                  <p className="text-xs text-slate-400">{c.industry || "View ad creatives"}</p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    );
  }

  // Client detail view
  return (
    <div data-testid="ad-creative-page">
      {/* Full-screen modal preview */}
      {previewCreative && (
        <AdPreviewModal
          creative={previewCreative}
          onClose={() => setPreviewCreative(null)}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setSelectedClient(null); setCreatives([]); cancelForm(); }}
            className="text-slate-500 hover:text-slate-900"
            data-testid="ad-creative-back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            All Clients
          </Button>
          <div className="h-6 w-px bg-slate-200" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">{selectedClient.name}</h1>
            <p className="text-sm text-slate-400">Ad Creative Galleries</p>
          </div>
        </div>
        <Button
          onClick={() => { cancelForm(); setShowAdd(true); }}
          className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-5"
          data-testid="add-ad-creative-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          Add Gallery Link
        </Button>
      </div>

      {/* Add / Edit form */}
      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mb-6"
          >
            <div className="bg-white rounded-xl p-5 border border-violet-200 shadow-sm">
              <p className="font-semibold text-slate-900 mb-4">
                {editingId ? "Edit Gallery Link" : "New Gallery Link"}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Label *</label>
                  <Input
                    value={form.label}
                    onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
                    placeholder="e.g., March Stories"
                    data-testid="ad-creative-label-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Adpiler URL *</label>
                  <Input
                    value={form.url}
                    onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
                    placeholder="https://preview.adpiler.com/..."
                    data-testid="ad-creative-url-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Campaign Type</label>
                  <select
                    value={form.campaign_type}
                    onChange={(e) => setForm((p) => ({ ...p, campaign_type: e.target.value, ad_name: "" }))}
                    className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500"
                    data-testid="ad-creative-type-select"
                  >
                    {CAMPAIGN_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Link to Line Item</label>
                  {adNames.length > 0 ? (
                    <AdNamePicker
                      value={form.ad_name}
                      onChange={(v) => setForm((p) => ({ ...p, ad_name: v }))}
                      options={adNames}
                    />
                  ) : (
                    <Input
                      value={form.ad_name}
                      onChange={(e) => setForm((p) => ({ ...p, ad_name: e.target.value }))}
                      placeholder="No imported ads yet — import data first, or leave blank"
                      className="text-slate-400"
                      data-testid="ad-creative-adname-input"
                    />
                  )}
                </div>
              </div>
              <div className="mb-4">
                <label className="text-xs font-medium text-slate-500 mb-1 block">Notes (optional)</label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  placeholder="Campaign details, date range, creative description..."
                  data-testid="ad-creative-notes-input"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleSave}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="ad-creative-save-btn"
                >
                  {editingId ? "Update" : "Add Gallery"}
                </Button>
                <Button variant="ghost" onClick={cancelForm}>Cancel</Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gallery content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
        </div>
      ) : creatives.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-dashed border-slate-300">
          <Layers className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No galleries yet</p>
          <p className="text-sm text-slate-400 mt-1">Add an Adpiler link to get started</p>
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map((group) => (
            <div key={group.value}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${group.color}`}>
                  {group.label}
                </span>
                <span className="text-xs text-slate-400">{group.items.length} {group.items.length === 1 ? "gallery" : "galleries"}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {group.items.map((c) => (
                  <motion.div
                    key={c.id}
                    whileHover={{ y: -2 }}
                    className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg hover:shadow-violet-500/5 transition-all group"
                    data-testid={`ad-creative-card-${c.id}`}
                  >
                    {/* Click to preview */}
                    <button
                      onClick={() => setPreviewCreative(c)}
                      className="w-full h-28 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center gap-2 cursor-pointer group-hover:from-violet-900 group-hover:to-slate-900 transition-all"
                      data-testid={`ad-creative-preview-${c.id}`}
                    >
                      <Eye className="w-5 h-5 text-slate-500 group-hover:text-violet-400 transition-colors" />
                      <span className="text-xs text-slate-500 group-hover:text-violet-400 transition-colors">
                        Preview
                      </span>
                    </button>

                    <div className="p-4">
                      <p className="font-semibold text-slate-900 text-sm truncate">{c.label}</p>
                      {c.ad_name ? (() => {
                        const imported = importedAdNamesByType[c.campaign_type];
                        const isOrphaned = imported && imported.size > 0 && !imported.has(c.ad_name.trim().toLowerCase());
                        if (isOrphaned) {
                          return (
                            <button
                              type="button"
                              onClick={() => startEdit(c)}
                              className="mt-0.5 w-full text-left flex items-center gap-1.5 group/orphan"
                              title={`"${c.ad_name}" is not in the current imported data. Click to re-link.`}
                              data-testid={`ad-creative-orphan-${c.id}`}
                            >
                              <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold flex-shrink-0">!</span>
                              <p className="text-xs text-amber-700 truncate group-hover/orphan:underline">
                                Broken link: {c.ad_name}
                              </p>
                            </button>
                          );
                        }
                        return (
                          <p className="text-xs text-violet-500 mt-0.5 truncate" title={c.ad_name}>Linked: {c.ad_name}</p>
                        );
                      })() : (
                        <p className="text-xs text-violet-500 mt-0.5 truncate" data-testid={`ad-creative-campaign-wide-${c.id}`}>
                          Linked: Entire campaign
                        </p>
                      )}
                      {c.notes && (
                        <p className="text-xs text-slate-400 mt-1 line-clamp-1">{c.notes}</p>
                      )}

                      <div className="flex items-center gap-1 mt-3 pt-3 border-t border-slate-100">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => startEdit(c)}
                          className="text-slate-400 hover:text-violet-600 h-7 px-2"
                          data-testid={`ad-creative-edit-${c.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <a
                          href={c.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-slate-400 hover:text-violet-600 h-7 px-2 rounded-md hover:bg-slate-50 transition-colors"
                          data-testid={`ad-creative-open-${c.id}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(c.id)}
                          className="text-slate-400 hover:text-red-500 h-7 px-2 ml-auto"
                          data-testid={`ad-creative-delete-${c.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// Searchable combobox for picking an imported ad name, with impressions context.
// Options: [{name, impressions, clicks, ctr}]. Falls back to plain text when
// the user wants something not in the list (preserves old behavior).
function AdNamePicker({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0, width: 360 });

  // Document-level click-outside listener — no full-screen overlay that could
  // block scroll events inside the popup.
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (popupRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Position the portaled popup right below the trigger, in viewport coords.
  // Re-measure on scroll/resize so the popup tracks its trigger.
  useEffect(() => {
    if (!open) return;
    const recalc = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      setPopupPos({
        top: r.bottom + 4,
        // Right-align with trigger but keep a minimum width of 360px
        left: Math.max(8, r.right - Math.max(r.width, 360)),
        width: Math.max(r.width, 360),
      });
    };
    recalc();
    window.addEventListener("resize", recalc);
    window.addEventListener("scroll", recalc, true);
    return () => {
      window.removeEventListener("resize", recalc);
      window.removeEventListener("scroll", recalc, true);
    };
  }, [open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, query]);

  const fmt = (n) => {
    if (n == null) return "—";
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
    if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
    return String(Math.round(n));
  };

  // Resolve the current value against options so we can show it nicely.
  const selected = options.find((o) => o.name === value);
  const hasCustomValue = !!value && !selected;

  return (
    <div className="relative" data-testid="ad-creative-adname-picker">
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500 flex items-center justify-between gap-2"
        data-testid="ad-creative-adname-trigger"
      >
        <span className="truncate text-left">
          {value ? (
            <>
              <span className="text-slate-900">{value}</span>
              {selected && (
                <span className="text-slate-400 font-normal"> · {fmt(selected.impressions)} imp</span>
              )}
              {hasCustomValue && (
                <span className="text-amber-600 font-normal"> · not in imported data</span>
              )}
            </>
          ) : (
            <span className="text-slate-400">Entire campaign (no specific ad)</span>
          )}
        </span>
        <ChevronLeft className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "-rotate-90" : "rotate-[-90deg]"}`} />
      </button>

      {open && createPortal(
        <div
          ref={popupRef}
          className="bg-white rounded-md border border-slate-200 shadow-lg"
          style={{
            position: 'fixed',
            top: `${popupPos.top}px`,
            left: `${popupPos.left}px`,
            width: `${popupPos.width}px`,
            zIndex: 9999,
          }}
        >
            <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 sticky top-0 bg-white">
              <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <input
                autoFocus
                type="text"
                placeholder={`Search ${options.length} imported ads...`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="flex-1 text-sm outline-none bg-transparent"
                data-testid="ad-creative-adname-search"
              />
              <span className="text-[11px] text-slate-400 flex-shrink-0 tabular-nums">{filtered.length}/{options.length}</span>
            </div>
            <div
              className="overflow-y-auto py-1 relative"
              style={{ maxHeight: 'min(50vh, 340px)' }}
            >
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  setOpen(false);
                  setQuery("");
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${value === "" ? "bg-violet-50" : ""}`}
                data-testid="ad-creative-adname-option-all"
              >
                <Check className={`w-3.5 h-3.5 ${value === "" ? "text-violet-600" : "text-transparent"}`} />
                <span className="text-slate-700">Entire campaign (no specific ad)</span>
              </button>
              {filtered.length === 0 && (
                <div className="px-3 py-4 text-xs text-slate-400 text-center">
                  No matches. Clear search to see all {options.length} imported ads.
                </div>
              )}
              {filtered.map((o) => (
                <button
                  key={o.name}
                  type="button"
                  onClick={() => {
                    onChange(o.name);
                    setOpen(false);
                    setQuery("");
                  }}
                  title={o.name}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${value === o.name ? "bg-violet-50" : ""}`}
                  data-testid={`ad-creative-adname-option-${o.name.replace(/\s+/g, "-")}`}
                >
                  <Check className={`w-3.5 h-3.5 flex-shrink-0 ${value === o.name ? "text-violet-600" : "text-transparent"}`} />
                  <span className="flex-1 min-w-0 text-slate-800 truncate">{o.name}</span>
                  {o.impressions > 0 ? (
                    <span className="text-[11px] text-slate-400 tabular-nums flex-shrink-0">{fmt(o.impressions)} imp</span>
                  ) : (
                    <span className="text-[11px] text-slate-300 flex-shrink-0">no data yet</span>
                  )}
                </button>
              ))}
            </div>
        </div>,
        document.body
      )}
    </div>
  );
}
